<template>
  <header class="header">
    <router-link to="/player/lobby" class="header-logo">
      <div class="logo-gem"><AppIcon name="nav-gem" :size="24" /></div>
      <span class="logo-name">Casino<span>Script</span></span>
    </router-link>

    <div class="header-search">
      <span class="search-icon"><AppIcon name="nav-search" :size="16" /></span>
      <input
        v-model="searchQuery"
        type="text"
        class="search-input"
        :placeholder="t('nav.search_games')"
        @input="onSearchInput"
        @keydown.enter="navigateToLobbyWithSearch"
      />
    </div>

    <router-link to="/player/wallet" class="header-balance">
      <span class="hb-currency"><AppIcon name="nav-balance" :size="16" /></span>
      <span class="hb-amount">{{ userStore.displayBalance }}</span>
    </router-link>

    <router-link to="/player/wallet" class="btn-deposit">{{ t('nav.deposit_btn') }}</router-link>

    <LanguageSwitcher />

    <div class="header-notif-wrap" ref="notifRef">
      <button class="header-notif" @click="toggleNotifDropdown" aria-label="Notifications">
        <AppIcon name="nav-bell" :size="20" />
        <span v-if="notificationStore.unreadCount > 0" class="notif-badge">
          {{ notificationStore.unreadCount > 99 ? '99+' : notificationStore.unreadCount }}
        </span>
      </button>
      <Transition name="dropdown">
        <div v-if="notifDropdownOpen" class="notif-dropdown">
          <div class="notif-dropdown-header">
            <span class="notif-dropdown-title">{{ t('nav.notifications') }}</span>
            <button
              v-if="notificationStore.unreadCount > 0"
              class="notif-mark-all"
              @click="handleMarkAllRead"
            >
              {{ t('nav.mark_all_read') }}
            </button>
          </div>
          <div v-if="recentLoading" class="notif-dropdown-loading">{{ t('common.loading') }}</div>
          <div v-else-if="recentNotifications.length === 0" class="notif-dropdown-empty">
            {{ t('nav.no_notifications') }}
          </div>
          <template v-else>
            <div
              v-for="n in recentNotifications"
              :key="n.id"
              class="notif-dropdown-item"
              :class="{ unread: !n.read_at }"
              @click="handleNotifClick(n)"
            >
              <div class="notif-dropdown-icon"><AppIcon :name="typeIcon(n.type)" :size="18" /></div>
              <div class="notif-dropdown-body">
                <div class="notif-dropdown-item-title">{{ n.title }}</div>
                <div class="notif-dropdown-item-time">{{ timeAgo(n.created_at) }}</div>
              </div>
            </div>
          </template>
          <router-link
            to="/player/notifications"
            class="notif-dropdown-footer"
            @click="closeNotifDropdown"
          >
            {{ t('nav.see_all') }}
          </router-link>
        </div>
      </Transition>
    </div>

    <div class="header-avatar-wrap" ref="avatarRef">
      <button class="header-avatar" @click="toggleDropdown" aria-label="Account menu">
        {{ userStore.initials }}
      </button>
      <Transition name="dropdown">
        <div v-if="dropdownOpen" class="avatar-dropdown">
          <router-link to="/player/profile" class="dropdown-item" @click="closeDropdown">
            <span class="dropdown-icon"><AppIcon name="nav-user" :size="16" /></span>
            {{ t('nav.profile') }}
          </router-link>
          <router-link to="/player/profile" class="dropdown-item" @click="closeDropdown">
            <span class="dropdown-icon"><AppIcon name="nav-settings" :size="16" /></span>
            {{ t('nav.settings') }}
          </router-link>
          <router-link to="/player/wallet" class="dropdown-item" @click="closeDropdown">
            <span class="dropdown-icon"><AppIcon name="nav-wallet" :size="16" /></span>
            {{ t('nav.wallet') }}
          </router-link>
          <div class="dropdown-divider"></div>
          <button class="dropdown-item dropdown-item--danger" @click="handleLogout">
            <span class="dropdown-icon"><AppIcon name="nav-logout" :size="16" /></span>
            {{ t('nav.logout') }}
          </button>
        </div>
      </Transition>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AppIcon from '@/components/AppIcon.vue'
import LanguageSwitcher from '@/components/LanguageSwitcher.vue'
import { useUserStore } from '@/stores/user'
import { useGamesStore } from '@/stores/games'
import { useNotificationStore } from '@/stores/notifications'
import { useWalletStore } from '@/stores/wallet'
import type { Notification } from '@/stores/notifications'
import { subscribeToPrivateChannel, unsubscribe } from '@/composables/useEcho'

const router = useRouter()
const { t } = useI18n()
const userStore = useUserStore()
const gamesStore = useGamesStore()
const notificationStore = useNotificationStore()
const walletStore = useWalletStore()

// Header search — two-way sync with store
const searchQuery = ref(gamesStore.searchQuery)

watch(() => gamesStore.searchQuery, (val) => {
  if (val !== searchQuery.value) searchQuery.value = val
})

function onSearchInput() {
  gamesStore.searchQuery = searchQuery.value
  navigateToLobbyIfNeeded()
}

function navigateToLobbyWithSearch() {
  gamesStore.searchQuery = searchQuery.value
  navigateToLobbyIfNeeded()
}

function navigateToLobbyIfNeeded() {
  const path = router.currentRoute.value.path
  if (!path.startsWith('/player/lobby') && !path.startsWith('/player/games')) {
    router.push({ name: 'player.lobby' })
  }
}

// Avatar dropdown
const dropdownOpen = ref(false)
const avatarRef = ref<HTMLElement | null>(null)

function toggleDropdown() {
  dropdownOpen.value = !dropdownOpen.value
}

function closeDropdown() {
  dropdownOpen.value = false
}

async function handleLogout() {
  closeDropdown()
  await userStore.logout()
  router.push('/login')
}

// Notification dropdown
const notifDropdownOpen = ref(false)
const notifRef = ref<HTMLElement | null>(null)
const recentNotifications = ref<Notification[]>([])
const recentLoading = ref(false)
let pollInterval: ReturnType<typeof setInterval> | null = null

function toggleNotifDropdown() {
  notifDropdownOpen.value = !notifDropdownOpen.value
  if (notifDropdownOpen.value) {
    fetchRecentNotifications()
  }
}

function closeNotifDropdown() {
  notifDropdownOpen.value = false
}

async function fetchRecentNotifications() {
  recentLoading.value = true
  try {
    await notificationStore.fetchNotifications(1)
    recentNotifications.value = notificationStore.notifications.slice(0, 5)
  } catch {
    // Silently fail — dropdown will show empty state
  } finally {
    recentLoading.value = false
  }
}

async function handleNotifClick(n: Notification) {
  if (!n.read_at) {
    await notificationStore.markRead(n.id)
    const item = recentNotifications.value.find((r) => r.id === n.id)
    if (item) {
      item.read_at = new Date().toISOString()
    }
  }
}

async function handleMarkAllRead() {
  await notificationStore.markAllRead()
  recentNotifications.value.forEach((n) => {
    if (!n.read_at) {
      n.read_at = new Date().toISOString()
    }
  })
}

function typeIcon(type: string): string {
  const map: Record<string, string> = {
    deposit: 'notif-deposit',
    withdrawal: 'notif-withdrawal',
    bonus: 'notif-bonus',
    vip: 'notif-vip',
    system: 'notif-system',
  }
  return map[type] ?? 'notif-system'
}

function timeAgo(iso: string): string {
  const now = Date.now()
  const diff = now - new Date(iso).getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return t('nav.just_now')
  if (minutes < 60) return t('nav.minutes_ago', { n: minutes })
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return t('nav.hours_ago', { n: hours })
  const days = Math.floor(hours / 24)
  return t('nav.days_ago', { n: days })
}

// WebSocket real-time notifications
interface NewNotificationPayload {
  id: string
  type: string
  title: string
  message: string
  created_at: string
}

const userChannel = computed(() =>
  userStore.user ? `App.Models.User.${userStore.user.id}` : null,
)

function onNewNotification(data: NewNotificationPayload) {
  // Increment unread count
  notificationStore.unreadCount++

  // Prepend to the local recent notifications list (keep max 5)
  recentNotifications.value = [
    {
      id: data.id,
      type: data.type,
      title: data.title,
      message: data.message,
      read_at: null,
      created_at: data.created_at,
    },
    ...recentNotifications.value,
  ].slice(0, 5)
}

interface BalanceUpdatedPayload {
  balance: string
  bonus_balance: string
  currency: string
}

function onBalanceUpdated(data: BalanceUpdatedPayload) {
  // Parse decimal strings to numbers at the WebSocket boundary
  const bal = Number(data.balance)
  const bonusBal = Number(data.bonus_balance)

  // Update user store wallet for the header balance display
  if (userStore.wallet) {
    userStore.wallet = {
      ...userStore.wallet,
      balance: bal,
      bonus_balance: bonusBal,
      currency: data.currency,
    }
  }
  // Update wallet store if it's been initialized (for WalletPage)
  walletStore.updateBalanceFromEvent({
    balance: bal,
    bonus_balance: bonusBal,
    currency: data.currency,
  })
}

function subscribeToUserChannel() {
  const channel = userChannel.value
  if (!channel) return

  subscribeToPrivateChannel<NewNotificationPayload>(
    channel,
    '.notification.new',
    onNewNotification,
  )

  subscribeToPrivateChannel<BalanceUpdatedPayload>(
    channel,
    '.balance.updated',
    onBalanceUpdated,
  )
}

function unsubscribeFromUserChannel() {
  const channel = userChannel.value
  if (!channel) return
  unsubscribe(channel)
}

// Click-outside handler for both dropdowns
function handleClickOutside(e: MouseEvent) {
  if (avatarRef.value && !avatarRef.value.contains(e.target as Node)) {
    closeDropdown()
  }
  if (notifRef.value && !notifRef.value.contains(e.target as Node)) {
    closeNotifDropdown()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  // Initial unread count fetch
  notificationStore.fetchUnreadCount()
  // Subscribe to private channel for real-time notifications
  subscribeToUserChannel()
  // Fallback: poll unread count every 30 seconds in case WebSocket is unavailable
  pollInterval = setInterval(() => {
    notificationStore.fetchUnreadCount()
  }, 30000)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  unsubscribeFromUserChannel()
  if (pollInterval) {
    clearInterval(pollInterval)
    pollInterval = null
  }
})
</script>

<style scoped>
.header {
  height: var(--header-height, 60px);
  background: rgba(11, 21, 32, 0.96);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 12px;
  position: relative;
  z-index: 200;
  flex-shrink: 0;
}

/* Logo */
.header-logo {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-family-display, 'Russo One', sans-serif);
  font-size: 17px;
  flex-shrink: 0;
  color: var(--text-primary, #fff);
  text-decoration: none;
}

.logo-gem {
  width: 30px;
  height: 30px;
  background: var(--accent-primary, #00e701);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-on-accent, #000);
  font-size: 14px;
  font-weight: 900;
  box-shadow: var(--shadow-glow-green, 0 0 20px rgba(0, 231, 1, 0.3));
  animation: glow 3s ease infinite;
}

.logo-name span {
  color: var(--accent-primary, #00e701);
}

/* Search */
.header-search {
  flex: 1;
  max-width: 320px;
  margin-left: auto;
  position: relative;
  display: flex;
  align-items: center;
}

.search-input {
  width: 100%;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  padding: 8px 12px 8px 36px;
  color: var(--text-primary, #fff);
  font-size: 13px;
  transition: border-color 150ms ease;
}

.search-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.search-input::placeholder {
  color: var(--text-tertiary, #7f8fa4);
}

.search-icon {
  position: absolute;
  left: 10px;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
  pointer-events: none;
}

/* Balance */
.header-balance {
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  padding: 7px 12px;
  cursor: pointer;
  transition: all 150ms ease;
  text-decoration: none;
  flex-shrink: 0;
}

.header-balance:hover {
  background: var(--bg-hover, #2a4455);
  border-color: rgba(255, 255, 255, 0.15);
}

.hb-currency {
  font-size: 13px;
}

.hb-amount {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 13px;
  font-weight: 700;
  color: var(--accent-primary, #00e701);
}

/* Deposit button */
.btn-deposit {
  padding: 9px 18px;
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
  font-size: 13px;
  font-weight: 700;
  border-radius: var(--radius-md, 8px);
  transition: all 150ms ease;
  letter-spacing: 0.02em;
  flex-shrink: 0;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
}

.btn-deposit:hover {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: var(--shadow-glow-green, 0 0 20px rgba(0, 231, 1, 0.3));
  transform: translateY(-1px);
}

/* Notification bell */
.header-notif {
  width: 34px;
  height: 34px;
  background: var(--bg-tertiary, #213743);
  border-radius: var(--radius-md, 8px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  cursor: pointer;
  position: relative;
  transition: background 150ms ease;
  flex-shrink: 0;
  border: none;
}

.header-notif:hover {
  background: var(--bg-hover, #2a4455);
}

.header-notif-wrap {
  position: relative;
  flex-shrink: 0;
}

.notif-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  background: var(--color-danger, #ed4245);
  border-radius: 8px;
  border: 1.5px solid rgba(11, 21, 32, 0.96);
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

/* Notification dropdown */
.notif-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 320px;
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-md, 8px);
  box-shadow: var(--shadow-lg, 0 8px 24px rgba(0, 0, 0, 0.5));
  z-index: 300;
  overflow: hidden;
}

.notif-dropdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.notif-dropdown-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary, #fff);
}

.notif-mark-all {
  font-size: 11px;
  font-weight: 600;
  color: var(--accent-secondary, #1475e1);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: opacity 150ms ease;
}

.notif-mark-all:hover {
  opacity: 0.8;
}

.notif-dropdown-loading,
.notif-dropdown-empty {
  padding: 24px 14px;
  text-align: center;
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
}

.notif-dropdown-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 150ms ease;
}

.notif-dropdown-item:hover {
  background: var(--bg-tertiary, #213743);
}

.notif-dropdown-item.unread {
  background: rgba(0, 231, 1, 0.04);
}

.notif-dropdown-item.unread:hover {
  background: rgba(0, 231, 1, 0.08);
}

.notif-dropdown-icon {
  font-size: 16px;
  flex-shrink: 0;
  width: 24px;
  text-align: center;
  margin-top: 1px;
}

.notif-dropdown-body {
  flex: 1;
  min-width: 0;
}

.notif-dropdown-item-title {
  font-size: 12.5px;
  font-weight: 500;
  color: var(--text-secondary, #b1bad3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notif-dropdown-item.unread .notif-dropdown-item-title {
  color: var(--text-primary, #fff);
  font-weight: 600;
}

.notif-dropdown-item-time {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  margin-top: 2px;
}

.notif-dropdown-footer {
  display: block;
  text-align: center;
  padding: 10px 14px;
  font-size: 12px;
  font-weight: 600;
  color: var(--accent-secondary, #1475e1);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  text-decoration: none;
  transition: background 150ms ease;
}

.notif-dropdown-footer:hover {
  background: var(--bg-tertiary, #213743);
}

/* Avatar & Dropdown */
.header-avatar-wrap {
  position: relative;
  flex-shrink: 0;
}

.header-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--gradient-avatar, linear-gradient(135deg, #1475e1, #9b59b6));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  border: 2px solid rgba(255, 255, 255, 0.1);
  transition: border-color 150ms ease;
  color: var(--text-primary, #fff);
}

.header-avatar:hover {
  border-color: var(--accent-primary, #00e701);
}

.avatar-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 180px;
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-md, 8px);
  padding: 6px;
  box-shadow: var(--shadow-lg, 0 8px 24px rgba(0, 0, 0, 0.5));
  z-index: 300;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border-radius: var(--radius-sm, 4px);
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary, #b1bad3);
  transition: all 150ms ease;
  cursor: pointer;
  text-decoration: none;
  border: none;
  background: none;
  text-align: left;
}

.dropdown-item:hover {
  background: var(--bg-tertiary, #213743);
  color: var(--text-primary, #fff);
}

.dropdown-item--danger:hover {
  color: var(--color-danger, #ed4245);
}

.dropdown-icon {
  font-size: 14px;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
}

.dropdown-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  margin: 4px 0;
}

/* Dropdown transition */
.dropdown-enter-active {
  animation: fadeIn 150ms ease forwards;
}
.dropdown-leave-active {
  animation: fadeOut 100ms ease forwards;
}

@keyframes glow {
  0%,
  100% {
    box-shadow: var(--shadow-glow-green, 0 0 20px rgba(0, 231, 1, 0.3));
  }
  50% {
    box-shadow: 0 0 40px rgba(0, 231, 1, 0.6);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-4px);
  }
}

/* Responsive: hide search and text on small screens */
@media (max-width: 768px) {
  .header-search {
    display: none;
  }

  .header-balance {
    margin-left: auto;
  }

  .btn-deposit {
    padding: 9px 12px;
    font-size: 12px;
  }

  .logo-name {
    display: none;
  }
}

/* RTL Support */
/* Search bar: push away from logo (which is now on the right in RTL) */
[dir='rtl'] .header-search {
  margin-left: 0;
  margin-right: auto;
}

/* Search icon: flip to right side inside the input */
[dir='rtl'] .search-icon {
  left: unset;
  right: 10px;
}

/* Search input: swap left/right padding so text doesn't sit under the icon */
[dir='rtl'] .search-input {
  padding: 8px 36px 8px 12px;
}

/* Dropdowns: anchor to the left in RTL (buttons are on the left side of the header) */
[dir='rtl'] .avatar-dropdown {
  right: unset;
  left: 0;
}

[dir='rtl'] .notif-dropdown {
  right: unset;
  left: 0;
}

@media (max-width: 768px) {
  [dir='rtl'] .header-balance {
    margin-left: 0;
    margin-right: auto;
  }
}
</style>
